/**
 * ActionToolbar Component
 * Left-panel toolbar for video analysis and app generation actions
 */

import { useState } from 'react';
import {
  Stack,
  Title,
  Text,
  Button,
  Select,
  TextInput,
  Group,
  Badge,
  Loader,
  Alert,
  Divider,
  Card
} from '@mantine/core';
import {
  IconVideo,
  IconChartBar,
  IconKey,
  IconTable,
  IconBrain,
  IconSparkles,
  IconAlertCircle,
  IconCoin
} from '@tabler/icons-react';
import { api } from '../../lib/api';
import modes from '../../lib/modes';
import type { AnalysisResult, Generation, AnalysisMode } from '../../lib/types';
import { calculateCredits } from '../../lib/utils';

export interface ActionToolbarProps {
  videoUrl: string;
  onActionStart: () => void;
  onActionComplete: (result: AnalysisResult | Generation) => void;
}

export default function ActionToolbar({ 
  videoUrl, 
  onActionStart, 
  onActionComplete 
}: ActionToolbarProps) {
  const [selectedMode, setSelectedMode] = useState<string>('Key moments');
  const [customInput, setCustomInput] = useState<string>('');
  const [subMode, setSubMode] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Analysis modes data
  const modeOptions = Object.keys(modes).map(key => ({ value: key, label: key }));
  const currentMode = modes[selectedMode as keyof typeof modes] as AnalysisMode;
  const hasSubModes = currentMode?.subModes && Object.keys(currentMode.subModes).length > 0;

  // Get icon for mode
  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'Key moments': return <IconKey size={16} />;
      case 'Chart': return <IconChartBar size={16} />;
      case 'Table': return <IconTable size={16} />;
      case 'Custom': return <IconBrain size={16} />;
      default: return <IconVideo size={16} />;
    }
  };

  const handleAnalyze = async () => {
    if (!videoUrl || !selectedMode) return;

    setIsAnalyzing(true);
    setError(null);
    onActionStart();

    try {
      const options: any = {};
      
      // Handle custom input for modes that need it
      if (selectedMode === 'Custom' && customInput.trim()) {
        options.customPrompt = customInput.trim();
      }
      
      // Handle sub-modes for Chart mode
      if (selectedMode === 'Chart' && subMode) {
        options.chartType = subMode;
      }

      const response = await api.analyzeVideo(videoUrl, selectedMode, options);
      
      if (response.success && response.data) {
        // Create AnalysisResult object
        const analysisResult: AnalysisResult = {
          id: `analysis_${Date.now()}`, // In real app, this would come from backend
          projectId: '', // Would be filled by parent component
          mode: selectedMode as any,
          resultData: response.data || [] as any,
          createdAt: new Date().toISOString(),
        };
        
        onActionComplete(analysisResult);
      } else {
        throw new Error(response.error || 'Analysis failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerateApp = async () => {
    if (!videoUrl) return;

    setIsGenerating(true);
    setError(null);
    onActionStart();

    try {
      const response = await api.generateLearningApp(videoUrl);
      
      if (response.success && response.data) {
        const generation: Generation = {
          id: `generation_${Date.now()}`,
          projectId: '',
          status: 'completed' as const,
          generatedSpec: JSON.stringify(response.data.spec),
          generatedCode: response.data.code,
          createdAt: new Date().toISOString(),
        };
        
        onActionComplete(generation);
      } else {
        throw new Error(response.error || 'App generation failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'App generation failed');
    } finally {
      setIsGenerating(false);
    }
  };

  const analysisCredits = calculateCredits(selectedMode.toLowerCase().replace(' ', '-'));
  const generationCredits = calculateCredits('learning-app');

  return (
    <Stack gap="md">
      {/* Error Alert */}
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Video Analysis Section */}
      <Card shadow="sm" padding="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Title order={4}>Analyze Video</Title>
            <Badge 
              leftSection={<IconCoin size={12} />}
              size="sm" 
              variant="light"
            >
              {analysisCredits} credit{analysisCredits !== 1 ? 's' : ''}
            </Badge>
          </Group>
          
          <Text size="sm" color="dimmed">
            Extract insights and data from the video content
          </Text>

          <Select
            label="Analysis Mode"
            placeholder="Choose analysis type"
            data={modeOptions}
            value={selectedMode}
            onChange={(value) => setSelectedMode(value || 'Key moments')}
            leftSection={getModeIcon(selectedMode)}
          />

          {/* Sub-mode selection for Chart mode */}
          {hasSubModes && (
            <Select
              label="Chart Type"
              placeholder="Choose chart type"
              data={Object.entries(currentMode.subModes!).map(([key]) => ({
                value: key,
                label: key,
              }))}
              value={subMode}
              onChange={(value) => setSubMode(value || '')}
            />
          )}

          {/* Custom input for Custom mode */}
          {selectedMode === 'Custom' && (
            <TextInput
              label="Custom Instructions"
              placeholder="Describe what you want to extract from the video..."
              value={customInput}
              onChange={(e) => setCustomInput(e.currentTarget.value)}
            />
          )}

          <Button
            fullWidth
            loading={isAnalyzing}
            disabled={isGenerating || !videoUrl || (selectedMode === 'Custom' && !customInput.trim())}
            onClick={handleAnalyze}
            leftSection={isAnalyzing ? <Loader size={16} /> : getModeIcon(selectedMode)}
          >
            {isAnalyzing ? 'Analyzing...' : 'Analyze Video'}
          </Button>
        </Stack>
      </Card>

      <Divider />

      {/* App Generation Section */}
      <Card shadow="sm" padding="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Title order={4}>Generate App</Title>
            <Badge 
              leftSection={<IconCoin size={12} />}
              size="sm" 
              variant="light" 
              color="orange"
            >
              {generationCredits} credits
            </Badge>
          </Group>
          
          <Text size="sm" color="dimmed">
            Create an interactive learning application from the video
          </Text>

          <Button
            fullWidth
            loading={isGenerating}
            disabled={isAnalyzing || !videoUrl}
            onClick={handleGenerateApp}
            leftSection={isGenerating ? <Loader size={16} /> : <IconSparkles size={16} />}
            variant="gradient"
            gradient={{ from: 'electric-cyan', to: 'warm-gold' }}
          >
            {isGenerating ? 'Generating App...' : 'Generate Learning App'}
          </Button>
        </Stack>
      </Card>
    </Stack>
  );
}